"use client";
import React, { useEffect, useRef, useState } from "react";
import { Avatar, Box, IconButton, SxProps } from "@mui/material";
import { CiCirclePlus } from "react-icons/ci";
import { MdClose } from "react-icons/md";
import { useDropzone } from "react-dropzone";

interface IFileInput {
  url?: string;
  wrapperSx?: SxProps;
  multiple?: boolean;
  accept?: string;
  onChange?: (file: FileList | undefined) => void;
  renderDisplay?: (props: { openSelectFile: () => void }) => React.ReactNode;
}

function FileInput({
  url,
  wrapperSx,
  multiple = true,
  accept,
  onChange = () => {},
  renderDisplay,
}: IFileInput) {
  const [imageUrl, setImageUrl] = useState<string>(url || "");
  const inputFileRef = useRef<HTMLInputElement | null>(null);

  function onDrop<T extends File>(acceptedFiles: T[]) {
    handleFileChange(acceptedFiles);
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
  const { onClick, onBlur, onFocus, onKeyDown, ...rootProps } = getRootProps();

  const handleOpenSelectFile = () => {
    inputFileRef.current?.click();
  };

  const handleDeleteFile = () => {
    onChange(undefined);
    setImageUrl("");
  };

  const handleFileChange = (files: any) => {
    if (!files) return;
    onChange(files);
  };

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  useEffect(() => {
    setImageUrl(url || "");
  }, [url]);

  return (
    <>
      <input
        hidden
        type="file"
        {...getInputProps({
          ref: inputFileRef,
          accept,
          onChange: (e) => {
            handleFileChange(e.target.files);
          },
          multiple,
        })}
      />
      {!!renderDisplay ? (
        renderDisplay({ openSelectFile: handleOpenSelectFile })
      ) : (
        <Box
          sx={{
            width: "80px",
            height: "80px",
            borderRadius: "4px",
            border: isDragActive ? "2px dashed" : "1px dashed",
            borderColor: "primary.main",
            position: "relative",
            transform: isDragActive ? "scale(1.1)" : "",
            transition: "all linear 0.1s",
            ...wrapperSx,
          }}
          {...rootProps}
        >
          {imageUrl ? (
            <>
              <Avatar
                onClick={handleOpenSelectFile}
                src={imageUrl || url}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              />
              <IconButton
                onClick={handleDeleteFile}
                sx={{
                  position: "absolute",
                  top: "2px",
                  right: "2px",
                  backgroundColor: "whitish.pureWhite",
                  color: "#000",
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "whitish.pureWhite",
                  },
                }}
              >
                <MdClose size={12} />
              </IconButton>
            </>
          ) : (
            <IconButton
              onClick={handleOpenSelectFile}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "primary.main",
              }}
            >
              <CiCirclePlus size={20} />
            </IconButton>
          )}
        </Box>
      )}
    </>
  );
}

export default FileInput;
